import type { AdvisorDashboardSnapshot, AdvisorDealRisk, AdvisorDataQualityScore } from './crmAdvisorTools';

export interface AdvisorInsightsData {
  snapshot?: AdvisorDashboardSnapshot | null;
  atRiskDeals?: AdvisorDealRisk[];
  dataQuality?: {
    contacts: AdvisorDataQualityScore[];
    companies: AdvisorDataQualityScore[];
  } | null;
}

export interface AdvisorPromptOptions {
  consultantName: string;
  consultantTitle: string;
  organizationId: string;
  businessName?: string | null;
  timeframeLabel?: string;
  timezone?: string;
}

export interface AdvisorPromptBuild {
  system: string;
  user: string;
  fallback: string;
  summary: AdvisorInsightsSummary;
}

export interface AdvisorInsightsSummary {
  weightedPipeline?: number;
  bestCase?: number;
  commit?: number;
  coverageRatio?: number;
  nextQuarterProjection?: number;
  atRiskDealCount: number;
  highestRiskDeals: AdvisorDealRisk[];
  contactQualityAverage?: number;
  companyQualityAverage?: number;
}

const numberFormatter = new Intl.NumberFormat(undefined, { maximumFractionDigits: 1 });
const currencyFormatter = new Intl.NumberFormat(undefined, { style: 'currency', currency: 'USD', maximumFractionDigits: 0 });

function formatCurrency(amount?: number): string {
  if (amount === undefined || amount === null || Number.isNaN(amount)) {
    return 'N/A';
  }
  return currencyFormatter.format(amount);
}

function averageScore(scores: AdvisorDataQualityScore[] | undefined): number | undefined {
  if (!scores || scores.length === 0) return undefined;
  const total = scores.reduce((acc, item) => acc + (item.score || 0), 0);
  return total / scores.length;
}

function shouldSuggestColdEmail(summary: AdvisorInsightsSummary): boolean {
  // Suggest cold email if:
  // 1. Coverage ratio is below 3x (insufficient pipeline)
  if (summary.coverageRatio !== undefined && summary.coverageRatio < 3.0) return true;
  
  // 2. Many deals are at risk (need to fill pipeline with new prospects)
  if (summary.atRiskDealCount > 5) return true;
  
  // 3. Weighted pipeline is low relative to commit/best case
  if (summary.weightedPipeline !== undefined && summary.commit !== undefined) {
    if (summary.weightedPipeline < summary.commit * 1.5) return true;
  }
  
  return false;
}

function buildSummary(data: AdvisorInsightsData): AdvisorInsightsSummary {
  const snapshot = data.snapshot;
  const atRiskDeals = data.atRiskDeals || [];
  const contacts = data.dataQuality?.contacts || [];
  const companies = data.dataQuality?.companies || [];

  return {
    weightedPipeline: snapshot?.totalWeighted,
    bestCase: snapshot?.bestCase,
    commit: snapshot?.commit,
    coverageRatio: snapshot?.coverageRatio,
    nextQuarterProjection: snapshot?.nextQuarterProjection,
    atRiskDealCount: atRiskDeals.length,
    highestRiskDeals: atRiskDeals.slice(0, 5),
    contactQualityAverage: averageScore(contacts),
    companyQualityAverage: averageScore(companies),
  };
}

export function buildAdvisorPrompt(data: AdvisorInsightsData, options: AdvisorPromptOptions): AdvisorPromptBuild {
  const summary = buildSummary(data);

  const { snapshot, atRiskDeals = [], dataQuality } = data;

  const systemContext = [
    `You are ${options.consultantName}, an expert ${options.consultantTitle}.`,
    'You are assisting a sales leader with a CRM health review.',
    'Provide structured, actionable recommendations with short bullet lists.',
    'Always highlight top priorities, mitigation steps, and celebrate wins.',
    'When probabilities are mentioned, relate them to business impact (ARR, cashflow, forecast).',
    'If data is missing or limited, clearly note the gaps before advising.',
    'If pipeline coverage is below 3x or many deals are at risk, proactively suggest launching a cold email campaign to increase top-of-funnel.',
    'When suggesting cold email campaigns, offer to help create one right now in the chat.',
  ].join(' ');

  const lines: string[] = [];
  lines.push(`Organization: ${options.organizationId}`);
  if (options.businessName) lines.push(`Focus Account: ${options.businessName}`);
  if (options.timeframeLabel) lines.push(`Reporting Range: ${options.timeframeLabel}`);
  if (options.timezone) lines.push(`Timezone: ${options.timezone}`);
  lines.push('---');

  if (snapshot) {
    lines.push('Pipeline Summary:');
    lines.push(`- Weighted Pipeline: ${formatCurrency(snapshot.totalWeighted)}`);
    if (snapshot.commit !== undefined) lines.push(`- Commit: ${formatCurrency(snapshot.commit)}`);
    if (snapshot.bestCase !== undefined) lines.push(`- Best Case: ${formatCurrency(snapshot.bestCase)}`);
    if (snapshot.coverageRatio !== undefined) lines.push(`- Quota Coverage: ${numberFormatter.format(snapshot.coverageRatio)}x`);
    if (snapshot.nextQuarterProjection !== undefined) {
      lines.push(`- Next Quarter Projection: ${formatCurrency(snapshot.nextQuarterProjection)}`);
    }
    if (Array.isArray(snapshot.owners) && snapshot.owners.length > 0) {
      lines.push('- Forecast by Owner:');
      snapshot.owners.forEach((row) => {
        lines.push(`  • ${row.owner}: weighted ${formatCurrency(row.weighted)}, commit ${formatCurrency(row.commit)}`);
      });
    }
  } else {
    lines.push('Pipeline Summary: No forecast data available.');
  }

  lines.push('---');
  if (atRiskDeals.length > 0) {
    lines.push(`At-Risk Deals (${atRiskDeals.length}):`);
    atRiskDeals.slice(0, 8).forEach((deal) => {
      const parts: string[] = [];
      parts.push(`${deal.title} (owner: ${deal.owner || 'Unassigned'})`);
      if (deal.weightedAmount !== undefined) parts.push(`weighted ${formatCurrency(deal.weightedAmount)}`);
      if (deal.arrAmount !== undefined) parts.push(`ARR ${formatCurrency(deal.arrAmount)}`);
      if (deal.probability !== undefined) parts.push(`prob ${(deal.probability * 100).toFixed(0)}%`);
      parts.push(`last updated ${deal.lastUpdated}`);
      lines.push(`- ${parts.join(' • ')}`);
    });
  } else {
    lines.push('At-Risk Deals: None identified.');
  }

  lines.push('---');
  
  // Add pipeline health alert if conditions are met
  if (shouldSuggestColdEmail(summary)) {
    lines.push('⚠️ PIPELINE HEALTH ALERT:');
    if (summary.coverageRatio !== undefined && summary.coverageRatio < 3.0) {
      lines.push(`- Coverage ratio is ${numberFormatter.format(summary.coverageRatio)}x (target: 3x+)`);
    }
    if (summary.atRiskDealCount > 5) {
      lines.push(`- ${summary.atRiskDealCount} deals at risk of stalling`);
    }
    if (summary.weightedPipeline !== undefined && summary.commit !== undefined && summary.weightedPipeline < summary.commit * 1.5) {
      lines.push(`- Weighted pipeline (${formatCurrency(summary.weightedPipeline)}) is below 1.5x commit`);
    }
    lines.push('RECOMMENDATION: Consider launching a cold email campaign to increase top-of-funnel leads.');
    lines.push('NOTE: I can help you create and launch a campaign right now if you\'d like.');
    lines.push('---');
  }
  
  if (dataQuality) {
    const contactAvg = summary.contactQualityAverage !== undefined ? `${numberFormatter.format(summary.contactQualityAverage)}%` : 'N/A';
    const companyAvg = summary.companyQualityAverage !== undefined ? `${numberFormatter.format(summary.companyQualityAverage)}%` : 'N/A';
    lines.push('Data Quality Overview:');
    lines.push(`- Avg Contact Score: ${contactAvg} (${(dataQuality.contacts || []).length} contacts assessed)`);
    lines.push(`- Avg Company Score: ${companyAvg} (${(dataQuality.companies || []).length} companies assessed)`);

    const lowContacts = (dataQuality.contacts || []).filter((item) => item.score < 60).slice(0, 5);
    if (lowContacts.length > 0) {
      lines.push('- Lowest Contact Scores:');
      lowContacts.forEach((item) => {
        lines.push(`  • ${item.name || item.entityId}: ${item.score}% (missing: ${item.missingFields.join(', ') || 'None'})`);
      });
    }

    const lowCompanies = (dataQuality.companies || []).filter((item) => item.score < 60).slice(0, 5);
    if (lowCompanies.length > 0) {
      lines.push('- Lowest Company Scores:');
      lowCompanies.forEach((item) => {
        lines.push(`  • ${item.name || item.entityId}: ${item.score}% (missing: ${item.missingFields.join(', ') || 'None'})`);
      });
    }
  } else {
    lines.push('Data Quality: No enrichment metrics available.');
  }

  const fallback = buildFallbackNarrative(summary);

  return {
    system: systemContext,
    user: lines.join('\n'),
    fallback,
    summary,
  };
}

function buildFallbackNarrative(summary: AdvisorInsightsSummary): string {
  const sections: string[] = [];

  sections.push('### CRM Advisor Insights (Fallback Summary)');

  if (summary.weightedPipeline !== undefined || summary.commit !== undefined) {
    const pipelineLines: string[] = [];
    if (summary.weightedPipeline !== undefined) pipelineLines.push(`Weighted pipeline stands at ${formatCurrency(summary.weightedPipeline)}.`);
    if (summary.commit !== undefined) pipelineLines.push(`Commit is ${formatCurrency(summary.commit)}.`);
    if (summary.coverageRatio !== undefined) pipelineLines.push(`Quota coverage is ${numberFormatter.format(summary.coverageRatio)}x.`);
    if (summary.nextQuarterProjection !== undefined) pipelineLines.push(`Projected next quarter pipeline is ${formatCurrency(summary.nextQuarterProjection)}.`);
    if (pipelineLines.length > 0) {
      sections.push('- **Pipeline**');
      pipelineLines.forEach((line) => sections.push(`  - ${line}`));
    }
  }

  sections.push('- **Deal Risks**');
  if (summary.atRiskDealCount > 0) {
    sections.push(`  - ${summary.atRiskDealCount} deals need attention. Focus on the highest risk items below.`);
    summary.highestRiskDeals.slice(0, 3).forEach((deal) => {
      const probability = deal.probability !== undefined ? `${(deal.probability * 100).toFixed(0)}%` : 'unknown probability';
      sections.push(`  - ${deal.title} (owner: ${deal.owner || 'Unassigned'}) • ${probability} • last touched ${deal.lastUpdated}`);
    });
  } else {
    sections.push('  - No deals are currently flagged as at-risk. Maintain cadence.');
  }

  sections.push('- **Data Hygiene**');
  if (summary.contactQualityAverage !== undefined || summary.companyQualityAverage !== undefined) {
    if (summary.contactQualityAverage !== undefined) {
      sections.push(`  - Avg contact data quality is ${numberFormatter.format(summary.contactQualityAverage)}%.`);
    }
    if (summary.companyQualityAverage !== undefined) {
      sections.push(`  - Avg company data quality is ${numberFormatter.format(summary.companyQualityAverage)}%.`);
    }
    sections.push('  - Prioritize filling critical gaps (email, phone, persona) for low-scoring records.');
  } else {
    sections.push('  - Enrichment scores are unavailable. Verify enrichment jobs and ensure integrations are active.');
  }

  sections.push('---');
  sections.push('Focus next actions on reviving the riskiest deals and improving data completeness for the worst-performing accounts.');

  return sections.join('\n');
}
